import React from 'react';
import { AlertCircle, Award, Calendar } from 'lucide-react';

/**
 * Компонент раздела дополнительных параметров
 */
const AdditionalParametersSection = ({ 
  formData, 
  handleInputChange, 
  handleSeasonalityChange,
  errors, 
  kbmOptions
}) => {
  // Массив месяцев для выбора сезонности
  const months = [
    { value: '1', label: 'Январь' },
    { value: '2', label: 'Февраль' },
    { value: '3', label: 'Март' },
    { value: '4', label: 'Апрель' },
    { value: '5', label: 'Май' },
    { value: '6', label: 'Июнь' },
    { value: '7', label: 'Июль' },
    { value: '8', label: 'Август' },
    { value: '9', label: 'Сентябрь' },
    { value: '10', label: 'Октябрь' },
    { value: '11', label: 'Ноябрь' },
    { value: '12', label: 'Декабрь' },
  ];
  
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <AlertCircle className="w-5 h-5 mr-2 text-emerald-500" />
        Дополнительные параметры
      </h3>
      
      <div className="space-y-4">
        {/* ДТП за прошлый период */}
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="hasAccidents"
              name="hasAccidents"
              checked={formData.hasAccidents}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="hasAccidents" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Были ДТП за прошлый страховой период
            </label>
          </div>
          
          {formData.hasAccidents && (
            <div className="ml-6 mt-2">
              <label htmlFor="accidentsCount" className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                Количество ДТП:
              </label>
              <input
                type="number"
                id="accidentsCount"
                name="accidentsCount"
                value={formData.accidentsCount}
                onChange={handleInputChange}
                min="1"
                max="10"
                className={`form-input w-full max-w-xs ${errors.accidentsCount ? 'input-error' : ''}`}
              />
              {errors.accidentsCount && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.accidentsCount}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Каждое ДТП увеличивает стоимость ОСАГО на 20%
              </p>
            </div>
          )}
        </div>

        {/* КБМ (коэффициент бонус-малус) */}
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="hasDiscounts"
              name="hasDiscounts"
              checked={formData.hasDiscounts}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="hasDiscounts" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Award className="w-4 h-4 mr-1 text-amber-500" />
              Применить коэффициент бонус-малус (КБМ)
            </label>
          </div>
          
          {formData.hasDiscounts && (
            <div className="ml-6 mt-2">
              <label htmlFor="kbmDiscount" className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                Класс КБМ:
              </label>
              <select
                id="kbmDiscount"
                name="kbmDiscount"
                value={formData.kbmDiscount}
                onChange={handleInputChange}
                className={`form-select w-full ${errors.kbmDiscount ? 'input-error' : ''}`}
              >
                <option value="">Выберите класс КБМ</option>
                {kbmOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.kbmDiscount && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.kbmDiscount}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Безаварийная езда в течение года позволяет получить скидку до 50%
              </p>
              <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-300">
                Чтобы узнать свой точный КБМ, проверьте его на сайте РСА или в своем действующем полисе ОСАГО
              </div>
            </div>
          )}
        </div>

        {/* Сезонность использования */}
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="hasSeasonality"
              name="hasSeasonality"
              checked={formData.hasSeasonality}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="hasSeasonality" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Calendar className="w-4 h-4 mr-1 text-emerald-500" />
              Сезонное использование ТС
            </label>
          </div>
          
          {formData.hasSeasonality && (
            <div className="ml-6 mt-2">
              <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">
                Выберите месяцы использования:
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {months.map(month => (
                  <label key={month.value} className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.seasonalityMonths.includes(month.value)}
                      onChange={(e) => handleSeasonalityChange(month.value, e.target.checked)}
                      className="form-checkbox"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {month.label}
                    </span>
                  </label>
                ))}
              </div>
              {errors.seasonalityMonths && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.seasonalityMonths}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Минимальный период использования - 3 месяца. Коэффициент рассчитывается в зависимости от количества месяцев.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdditionalParametersSection;